import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import useAuthProtection from "../hooks/useAuthProtection";
import Loader from "../components/Loader";
import ErrorState from "../components/ErrorState";
import EmptyState from "../components/EmptyState";
import {
  Container,
  Card,
  Button,
  Label,
  Input,
  Textarea,
  CountryLabel,
  SearchIcon,
} from "../components/ui";
import {
  useClaimCandidates,
  useCreateClaim,
  useUploadClaimDocument,
} from "../apiCalls/claimApiCalls";

// Two steps on one page: find the profile that is already in the register,
// then say enough about yourself that an admin can believe you.
export default function ClaimProfile() {
  useAuthProtection();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [claimedDob, setClaimedDob] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  // Set once the claim exists server-side. Submitting is two requests, and the
  // second can fail on its own — remembering the id is what lets the user
  // retry the upload instead of re-filing a claim the API would reject as a
  // duplicate.
  const [claimId, setClaimId] = useState(null);

  const { data: results, isLoading, isError } = useClaimCandidates(search);
  const candidates = results?.players;
  const { mutateAsync: createClaim, isLoading: isCreating } = useCreateClaim();
  const { mutateAsync: uploadDocument, isLoading: isUploading } =
    useUploadClaimDocument();

  // Covers both requests. Watching only the first would re-enable the button
  // while the document is still uploading.
  const isSubmitting = isCreating || isUploading;

  // The claim's details are fixed once it is filed; only the document is still
  // outstanding at that point.
  const detailsLocked = Boolean(claimId);

  const handleSubmit = async (event) => {
    event.preventDefault();

    // mutateAsync rejects on failure. The hooks already toast the reason, so
    // swallow it here — but stay on the page, because navigating away would
    // tell the user their claim went through when it didn't.
    try {
      let id = claimId;

      if (!id) {
        const claim = await createClaim({
          playerId: selected._id,
          claimedDob: claimedDob || null,
          phone: phone || null,
          note: note || null,
        });
        id = claim._id;
        setClaimId(id);
      }

      // The document is a second request: its Cloudinary id is derived from
      // the claim id, which does not exist until the claim does.
      if (file) {
        await uploadDocument({ id, file });
      }

      navigate("/profile");
    } catch {
      // Already surfaced by the mutation's onError toast.
    }
  };

  return (
    <Container className="py-10 md:py-12">
      <Card elevated className="max-w-3xl">
        <h1 className="text-3xl font-bold tracking-tight text-navy md:text-4xl">
          Claim your profile
        </h1>
        <p className="mt-3 text-muted">
          If you've played a ranked tournament you are probably already in our
          register. Find yourself below and tell us enough to prove it.
        </p>

        {!selected && (
          <div className="mt-8">
            <Label htmlFor="claim-search">Search the register by name</Label>
            <Input
              id="claim-search"
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Start typing your name…"
              icon={<SearchIcon />}
            />

            {search.trim().length < 2 && (
              <p className="mt-2 text-sm text-muted">
                Enter at least two characters.
              </p>
            )}

            {isLoading && search.trim().length >= 2 && (
              <Loader label="Searching the register…" />
            )}
            {isError && <ErrorState message="Could not search the register." />}

            {candidates && candidates.length === 0 && (
              <EmptyState message="Nobody unclaimed matches that name. If you've never played a ranked event, register as a new player instead." />
            )}

            {results?.truncated && (
              // Without this the list looks complete, and someone who is in
              // the register but below the cut concludes they are not — then
              // registers a duplicate profile nobody can merge back.
              <p className="mt-3 rounded-sm border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-navy">
                There are more matches than we can show. Add your surname, or
                more of it, to narrow the search — your profile may not be in
                the list below.
              </p>
            )}

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {(candidates || []).map((player) => (
                <button
                  key={player._id}
                  type="button"
                  onClick={() => setSelected(player)}
                  className="flex items-center gap-4 rounded-md border border-border bg-surface p-4 text-left transition-colors hover:border-primary hover:bg-primary-wash focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <img
                    src={
                      player.imgUrl ||
                      `${process.env.PUBLIC_URL}/default-user.jpg`
                    }
                    alt={player.name}
                    className="h-12 w-12 shrink-0 rounded-full object-cover"
                  />
                  {/* min-w-0 lets a long name truncate instead of pushing the
                      card past its grid column. */}
                  <span className="min-w-0">
                    <span className="block truncate font-semibold text-navy">
                      {player.name}
                    </span>
                    <span className="mt-0.5 flex items-center gap-2 text-sm text-muted">
                      <span className="truncate">
                        {player.club?.name || "No club"}
                      </span>
                      {player.nationality && (
                        <CountryLabel
                          code={player.nationality}
                          variant="compact"
                        />
                      )}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {selected && (
          <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-5">
            {claimId ? (
              // The claim landed; only the document failed. Say so, or the
              // user re-submits and meets a confusing duplicate-claim error.
              <div className="rounded-sm border border-gold/40 bg-gold/10 px-4 py-3 text-sm text-navy">
                Your claim on <strong>{selected.name}</strong> was submitted —
                only the photo ID didn't upload. Choose the file again and
                resubmit, or{" "}
                <Button
                  type="button"
                  variant="tertiary"
                  className="align-baseline underline"
                  onClick={() => navigate("/profile")}
                >
                  skip it for now
                </Button>
                .
              </div>
            ) : (
              <div className="rounded-sm border border-primary/20 bg-primary-wash px-4 py-3 text-sm text-navy">
                Claiming <strong>{selected.name}</strong>.{" "}
                <Button
                  type="button"
                  variant="tertiary"
                  className="align-baseline underline"
                  onClick={() => setSelected(null)}
                >
                  Choose someone else
                </Button>
              </div>
            )}

            <div>
              <Label htmlFor="claim-dob">Your date of birth</Label>
              <Input
                id="claim-dob"
                type="date"
                value={claimedDob}
                onChange={(event) => setClaimedDob(event.target.value)}
                required
                // Once the claim is filed these details are already with the
                // admin. Leaving them editable would invite corrections this
                // page has no way to send.
                disabled={detailsLocked}
              />
            </div>

            <div>
              <Label htmlFor="claim-phone">Phone number</Label>
              <Input
                id="claim-phone"
                type="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                placeholder="+00 00 000 0000"
                required
                disabled={detailsLocked}
              />
            </div>

            <div>
              <Label htmlFor="claim-note">
                Anything that helps us confirm it's you
              </Label>
              <Textarea
                id="claim-note"
                rows={3}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="e.g. I played the Madrid Open in March with Carlos."
                disabled={detailsLocked}
              />
            </div>

            <div>
              <Label htmlFor="claim-document">Photo ID</Label>
              <input
                id="claim-document"
                type="file"
                accept="image/*"
                aria-describedby="claim-document-hint"
                onChange={(event) => setFile(event.target.files[0])}
                // The native control is left as-is apart from its file button:
                // there is no cross-browser way to restyle the widget itself,
                // and replacing it with a label-triggered proxy would lose the
                // filename readout the user relies on after choosing a file.
                className="block w-full text-sm text-muted file:mr-4 file:cursor-pointer file:rounded-sm file:border-0 file:bg-primary-wash file:px-4 file:py-2 file:text-sm file:font-semibold file:text-primary hover:file:bg-primary/10"
                // On the retry pass the button promises to attach a document,
                // so it must not submit without one. "Skip it for now" in the
                // banner above is the deliberate way out.
                required={detailsLocked}
              />
              <span
                id="claim-document-hint"
                className="mt-2 block text-sm text-muted"
              >
                Only an administrator sees this, and we delete it as soon as
                your claim is decided.
              </span>
            </div>

            <Button type="submit" className="self-start" disabled={isSubmitting}>
              {isSubmitting
                ? "Submitting…"
                : claimId
                ? "Attach photo ID"
                : "Submit claim"}
            </Button>
          </form>
        )}
      </Card>
    </Container>
  );
}
